import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import {
  getAuthItem,
  setAuthItem,
  removeAuthItem,
  getAuthJson,
  setAuthJson,
} from "../utils/authStorage";
import {
  ADMIN_RBAC_CATALOG,
  ESS_SYSTEM_RBAC_CATALOG,
  normalizeAccess,
  ACTIONS,
  isLegacyEssScreenAccess,
  buildDefaultEssSystemRoleAccess,
  expandEssQuickPanelScreens,
} from "./catalog";

const PermissionsContext = createContext(null);
const RBAC_STORAGE_KEY = "rbacSnapshot";

// Minimum interval between two server refreshes. Guards against accidental
// re-fetches on rapid route changes / remounts. The snapshot is persisted in
// localStorage, so within this window we happily serve from cache.
const REFRESH_COOLDOWN_MS = 60 * 1000;

/** Admin landing (/admin/home) — not in RBAC catalog; always allowed when permissions are loaded. */
const HOME_MODULE_KEY = "home";

const ADMIN_MODULE_DEFS = new Map(ADMIN_RBAC_CATALOG.map((m) => [m.key, m]));

/** Every screen of `def` that offers `action` has it granted (false when no screen offers it). */
export const isWholeModuleGranted = (moduleAcc, def, action) => {
  const offering = (def?.screens || []).filter((s) => s.actions?.includes(action));
  return (
    offering.length > 0 &&
    offering.every((s) => moduleAcc?.screens?.[s.key]?.[action] === true)
  );
};

// Snapshot shape persisted in localStorage (fallback only):
// {
//   role:         "admin" | "super_admin" | "employee",
//   isAdmin:      boolean,
//   roleCode:     "ADMIN" | "EMPLOYEE" | "<custom>",
//   roleName:     string,
//   access:       normalizeAccess output,
//   fetchedAt:    number (ms)
// }

// Shapes an admin/super_admin as a wildcard-allow snapshot. Used as the fallback
// when the user has no customRoleCode (i.e. the system "admin" bucket) so they
// retain full access without any DB entries.
const buildAdminSnapshot = (role) => {
  const access = {};
  for (const mod of ADMIN_RBAC_CATALOG) {
    const moduleAcc = { screens: {} };
    for (const action of ACTIONS) moduleAcc[action] = true;
    for (const screen of mod.screens) {
      const screenAcc = {};
      for (const action of ACTIONS) screenAcc[action] = true;
      moduleAcc.screens[screen.key] = screenAcc;
    }
    access[mod.key] = moduleAcc;
  }
  return {
    role,
    isAdmin: true,
    roleCode: role === "super_admin" ? "SUPER_ADMIN" : "ADMIN",
    roleName: role === "super_admin" ? "Super Admin" : "Admin",
    access,
    fetchedAt: Date.now(),
  };
};

/**
 * Normalizes the raw screenAccess from /me/permissions into the snapshot shape.
 * Custom roles (e.g. FRONTEND) may carry both admin modules (employee, configuration…)
 * AND ESS modules. We normalize against both catalogs so hasAnyModuleAccess / hasScreenAccess
 * works correctly for both the Admin Sidebar top-nav and the ESS Sidebar.
 */
const normalizeEmployeeScreenAccess = (raw, roleCode) => {
  const code = String(roleCode || "EMPLOYEE").toUpperCase();
  let merged = raw && typeof raw === "object" ? { ...raw } : {};
  const qp = merged.ess_quick_panel;
  if (qp?.screens && typeof qp.screens === "object") {
    merged = {
      ...merged,
      ess_quick_panel: {
        ...qp,
        screens: expandEssQuickPanelScreens(qp.screens),
      },
    };
  }
  if ((code === "EMPLOYEE" || code === "MANAGER") && isLegacyEssScreenAccess(merged)) {
    merged = buildDefaultEssSystemRoleAccess(code);
  }
  // Switched-off Quick Panel: no raise / approval screen is granted.
  if (merged.ess_quick_panel?.enabled === false) {
    merged = { ...merged, ess_quick_panel: { enabled: false, screens: {} } };
  }
  // Custom roles use the stored matrix as-is. Legacy Action-screen expand is only
  // for system EMPLOYEE/MANAGER — otherwise a URM revoke (keys omitted on save)
  // gets re-seeded on the next employee login.
  const expandLegacyActions = code === "EMPLOYEE" || code === "MANAGER";
  const adminAccess = normalizeAccess(merged, ADMIN_RBAC_CATALOG, {
    expandLegacyActions,
  });
  // ESS modules are normalized last and take precedence (no key overlap with admin).
  const essAccess = normalizeAccess(merged, ESS_SYSTEM_RBAC_CATALOG, {
    expandLegacyActions: true,
  });
  return { ...adminAccess, ...essAccess };
};

const readSnapshotFromStorage = () => {
  try {
    const raw = getAuthItem(RBAC_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const catalog = parsed.isAdmin ? ADMIN_RBAC_CATALOG : ESS_SYSTEM_RBAC_CATALOG;
    const access = parsed.isAdmin
      ? normalizeAccess(parsed.access, catalog)
      : normalizeEmployeeScreenAccess(parsed.access, parsed.roleCode);
    return {
      ...parsed,
      access,
    };
  } catch {
    return null;
  }
};

const writeSnapshotToStorage = (snapshot) => {
  try {
    setAuthItem(RBAC_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // storage full / disabled; ignore
  }
};

const clearSnapshotFromStorage = () => {
  try {
    removeAuthItem(RBAC_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const buildSnapshotFromPermissionsResponse = (data) => {
  const role = String(data?.role || "employee").toLowerCase();
  const isAdmin = !!data?.isAdmin;
  if (isAdmin) return buildAdminSnapshot(role);
  return {
    role,
    isAdmin: false,
    roleCode: data?.roleCode || "EMPLOYEE",
    roleName: data?.roleName || "Employee",
    access: normalizeEmployeeScreenAccess(data?.screenAccess, data?.roleCode),
    dataAccess: data?.dataAccess || null,
    fetchedAt: Date.now(),
  };
};

// Utility consumed by LoginPage/api.js to seed the snapshot right after login
// so the first render already has permissions without a second HTTP round-trip.
export const persistRbacFromLogin = ({ user, userType, rbac }) => {
  const role = (user?.role || userType || "").toLowerCase();
  const isAdmin = role === "admin" || role === "super_admin";

  if (isAdmin) {
    writeSnapshotToStorage(buildAdminSnapshot(role));
  } else {
    const snapshot = {
      role: role || "employee",
      isAdmin: false,
      roleCode: rbac?.role?.code || user?.customRoleCode || "EMPLOYEE",
      roleName: rbac?.role?.name || "Employee",
      access: normalizeEmployeeScreenAccess(
        rbac?.screenAccess,
        rbac?.role?.code || user?.customRoleCode
      ),
      dataAccess: rbac?.dataAccess || null,
      fetchedAt: Date.now(),
    };
    writeSnapshotToStorage(snapshot);
  }
  // Notify any mounted PermissionsProvider to rehydrate from storage.
  try {
    window.dispatchEvent(new Event("rbac:updated"));
  } catch {
    // SSR/jsdom safety; ignore.
  }
};

export const clearRbacSnapshot = () => {
  clearSnapshotFromStorage();
  try {
    window.dispatchEvent(new Event("rbac:updated"));
  } catch {
    // ignore
  }
};

// Login-driven authoritative sync.
// Called by authService.login immediately after token + seed snapshot are set.
export const fetchAndPersistMyPermissions = async () => {
  const hasToken = !!getAuthItem("token");
  if (!hasToken) return readSnapshotFromStorage();
  try {
    const resp = await api.get("/global-settings/me/permissions");
    const data = resp?.data?.data;
    if (!data) throw new Error("Empty permissions response");
    const next = buildSnapshotFromPermissionsResponse(data);
    writeSnapshotToStorage(next);
    try {
      window.dispatchEvent(new Event("rbac:updated"));
    } catch {
      // ignore
    }
    return next;
  } catch (err) {
    console.warn("[RBAC] failed to sync /me/permissions at login:", err?.message || err);
    return readSnapshotFromStorage();
  }
};

export function PermissionsProvider({ children }) {
  const [snapshot, setSnapshot] = useState(() => readSnapshotFromStorage());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs so refresh() can stay a stable function (empty deps) and not cause a
  // churn of new function identities every time snapshot changes - that was
  // the root cause of /me/permissions being fired on every render/navigation.
  const snapshotRef = useRef(snapshot);
  const inFlightRef = useRef(null); // current in-flight promise (de-dupes)
  const lastFetchedAtRef = useRef(0); // timestamp of last successful fetch

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  // Pull the authoritative permissions from the backend. This is what makes
  // the gating strict: we ignore any stale localStorage values the moment the
  // server responds. localStorage only survives if the API call fails.
  //
  // The function is intentionally stable (empty deps). It:
  //   - no-ops if unauthenticated
  //   - de-dupes concurrent callers (single in-flight promise)
  //   - honours REFRESH_COOLDOWN_MS unless `force` is true
  const refresh = useCallback(async ({ force = false } = {}) => {
    const isAuthed = getAuthItem("isAuthenticated") === "true";
    if (!isAuthed) {
      setSnapshot(null);
      snapshotRef.current = null;
      clearSnapshotFromStorage();
      return null;
    }

    if (inFlightRef.current) return inFlightRef.current;

    const sinceLast = Date.now() - lastFetchedAtRef.current;
    if (!force && sinceLast < REFRESH_COOLDOWN_MS && snapshotRef.current) {
      return snapshotRef.current;
    }

    setLoading(true);
    setError(null);

    const promise = (async () => {
      try {
        const resp = await api.get("/global-settings/me/permissions");
        const data = resp?.data?.data;
        if (!data) throw new Error("Empty permissions response");

        const role = String(data.role || "employee").toLowerCase();
        const isAdmin = !!data.isAdmin;
        const next = isAdmin
          ? buildAdminSnapshot(role)
          : {
              role,
              isAdmin: false,
              roleCode: data.roleCode || "EMPLOYEE",
              roleName: data.roleName || "Employee",
              access: normalizeEmployeeScreenAccess(data.screenAccess, data.roleCode),
              dataAccess: data.dataAccess || null,
              fetchedAt: Date.now(),
            };

        lastFetchedAtRef.current = Date.now();
        snapshotRef.current = next;
        setSnapshot(next);
        writeSnapshotToStorage(next);
        // Keep currentUser.customRoleCode aligned so Approvals filters that
        // fall back to localStorage still match Client Access / custom roles.
        if (!isAdmin && next.roleCode) {
          try {
            const me = getAuthJson("currentUser", null);
            if (me && me.customRoleCode !== next.roleCode) {
              setAuthJson("currentUser", {
                ...me,
                customRoleCode: next.roleCode,
                roleCode: next.roleCode,
              });
            }
          } catch {
            /* ignore */
          }
        }
        return next;
      } catch (err) {
        console.warn("[RBAC] failed to refresh /me/permissions:", err?.message || err);
        setError(err);
        // Keep whatever is already in state/storage as a rare fallback.
        return snapshotRef.current;
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = promise;
    return promise;
  }, []);

  // On full page refresh with an existing authenticated session, fetch the
  // authoritative permissions once so context stays consistent with backend.
  // Role/permission edits for other open sessions require logout/login (or F5).
  useEffect(() => {
    const isAuthed = getAuthItem("isAuthenticated") === "true";
    const hasToken = !!getAuthItem("token");
    if (isAuthed && hasToken) {
      refresh({ force: true });
    }
  }, [refresh]);

  // Re-hydrate from localStorage whenever the login/logout flow dispatches
  // `rbac:updated`, and also when the user logs in via another tab (storage
  // event). Server sync is login-driven from authService.login, so this effect
  // only rehydrates the in-memory snapshot.
  useEffect(() => {
    const handleUpdate = () => {
      const next = readSnapshotFromStorage();

      // If snapshot was cleared (logout/session clear), just drop state.
      if (!next) {
        snapshotRef.current = null;
        setSnapshot(null);
        return;
      }

      snapshotRef.current = next;
      setSnapshot(next);
    };
    const onStorage = (e) => {
      if (e.key === RBAC_STORAGE_KEY || e.key === null) handleUpdate();
    };
    window.addEventListener("rbac:updated", handleUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("rbac:updated", handleUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // ------------------- Permission checks --------------------
  // These are intentionally cheap: they just index the pre-normalized map.
  // Admin modules: true only when the WHOLE module grants `action` (every screen that
  // offers it). Stored module flags are an OR across screens, so reading them directly
  // let one granted screen unlock every sibling screen ("screen || module" checks).
  const hasModuleAccess = useCallback(
    (moduleKey, action = "view") => {
      if (!snapshot) return false;
      if (snapshot.isAdmin) return true;
      if (moduleKey === HOME_MODULE_KEY) return true;
      const mod = snapshot.access?.[moduleKey];
      const def = ADMIN_MODULE_DEFS.get(moduleKey);
      if (!def) return !!mod?.[action];
      return isWholeModuleGranted(mod, def, action);
    },
    [snapshot]
  );

  const hasScreenAccess = useCallback(
    (moduleKey, screenKey, action = "view") => {
      if (!snapshot) return false;
      if (snapshot.isAdmin) return true;
      if (moduleKey === HOME_MODULE_KEY) {
        // If this role has no home screen permissions configured yet (all false),
        // default to showing everything for backward compatibility.
        const homeScreens = snapshot.access?.[HOME_MODULE_KEY]?.screens;
        const hasAnyHomePermission =
          homeScreens &&
          Object.values(homeScreens).some(
            (s) => s && Object.values(s).some((v) => v === true)
          );
        if (!hasAnyHomePermission) return true;
        // New ESS home widgets may not exist on older role payloads — inherit dashboard.
        if (homeScreens?.[screenKey] == null) {
          return !!homeScreens?.dashboard?.[action];
        }
        return !!homeScreens?.[screenKey]?.[action];
      }
      return !!snapshot.access?.[moduleKey]?.screens?.[screenKey]?.[action];
    },
    [snapshot]
  );

  // Any kind of access inside a module? Used to decide whether to show the
  // navbar tab when only a screen (not the whole module) was granted.
  const hasAnyModuleAccess = useCallback(
    (moduleKey) => {
      if (!snapshot) return false;
      if (snapshot.isAdmin) return true;
      if (moduleKey === HOME_MODULE_KEY) return true;
      const mod = snapshot.access?.[moduleKey];
      if (!mod) return false;
      if (ACTIONS.some((a) => mod[a] === true)) return true;
      const screens = mod.screens || {};
      return Object.values(screens).some(
        (s) => s && ACTIONS.some((a) => s[a] === true)
      );
    },
    [snapshot]
  );

  const value = useMemo(
    () => ({
      snapshot,
      loading,
      error,
      refresh,
      hasModuleAccess,
      hasScreenAccess,
      hasAnyModuleAccess,
      isAdmin: !!snapshot?.isAdmin,
      role: snapshot?.role || null,
      roleCode: snapshot?.roleCode || null,
      roleName: snapshot?.roleName || null,
    }),
    [
      snapshot,
      loading,
      error,
      refresh,
      hasModuleAccess,
      hasScreenAccess,
      hasAnyModuleAccess,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // During the initial render of App before provider mounts, or outside it,
    // return a safe "deny everything" object so consumers never crash.
    return {
      snapshot: null,
      loading: false,
      error: null,
      refresh: async () => null,
      hasModuleAccess: () => false,
      hasScreenAccess: () => false,
      hasAnyModuleAccess: () => false,
      isAdmin: false,
      role: null,
      roleCode: null,
      roleName: null,
    };
  }
  return ctx;
};

// Convenience hooks for consumers.
export const useModulePermission = (moduleKey, action = "view") => {
  const { hasModuleAccess } = usePermissions();
  return hasModuleAccess(moduleKey, action);
};

export const useScreenPermission = (moduleKey, screenKey, action = "view") => {
  const { hasScreenAccess } = usePermissions();
  return hasScreenAccess(moduleKey, screenKey, action);
};
